import { useEffect, useRef, useState } from "react";
import styles from "./CheckoutSection.module.css";

function AddressForm({ address = {}, setAddress, children = null }) {
    const addressInputRef = useRef(null);
    const [googleReady, setGoogleReady] = useState(Boolean(window.google?.maps?.places));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAddress((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    };

    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

        if (!apiKey) {
            return;
        }

        if (window.google?.maps?.places) {
            setGoogleReady(true);
            return;
        }

        const existingScript = document.getElementById("google-maps-script");

        const onGoogleReady = () => {
            setGoogleReady(Boolean(window.google?.maps?.places));
        };

        if (existingScript) {
            existingScript.addEventListener("load", onGoogleReady);
            return () => existingScript.removeEventListener("load", onGoogleReady);
        }

        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = onGoogleReady;
        document.body.appendChild(script);

        return () => {
            if (script) {
                script.removeEventListener("load", onGoogleReady);
            }
        };
    }, []);

    useEffect(() => {
        if (!googleReady || !addressInputRef.current) {
            return;
        }

        const input = addressInputRef.current;

        if (input.dataset.autocompleteBound === "true") {
            return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(input, {
            types: ["address"],
            fields: ["address_components", "formatted_address"],
        });

        const handlePlaceChanged = () => {
            const place = autocomplete.getPlace();
            if (!place || !place.address_components) return;

            const getComponent = (type) =>
                place.address_components.find((component) =>
                    component.types.includes(type)
                )?.long_name || "";

            const streetNumber = getComponent("street_number");
            const streetName = getComponent("route");
            const city =
                getComponent("locality") ||
                getComponent("postal_town") ||
                getComponent("administrative_area_level_3") ||
                "";
            const state = getComponent("administrative_area_level_1") || "";
            const zipCode = getComponent("postal_code") || "";

            setAddress((prevValues) => ({
                ...prevValues,
                address: [streetNumber, streetName].filter(Boolean).join(" ") || place.formatted_address || prevValues.address,
                city,
                state,
                zip: zipCode,
            }));
        };

        const listener = autocomplete.addListener("place_changed", handlePlaceChanged);
        input.dataset.autocompleteBound = "true";

        return () => {
            input.dataset.autocompleteBound = "false";
            if (listener) {
                window.google.maps.event.removeListener(listener);
            }
        };
    }, [googleReady, setAddress]);

    return (
        <form className={styles.addressForm}>

            <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={address.firstName || ""}
                onChange={handleChange}
            />

            <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={address.lastName || ""}
                onChange={handleChange}
            />
            <input
                ref={addressInputRef}
                type="text"
                name="address"
                placeholder="Address"
                value={address.address || ""}
                onChange={handleChange}
                className={styles.fullWidthField}
            />
            <input
                type="text"
                name="city"
                placeholder="City"
                value={address.city || ""}
                onChange={handleChange}
                className={styles.cityField}
            />
            <input
                type="text"
                name="state"
                placeholder="State"
                value={address.state || ""}
                onChange={handleChange}
            />
            <input
                type="text"
                name="zip"
                placeholder="ZIP Code"
                value={address.zip || ""}
                onChange={handleChange}
            />
            <input
                type="email"
                name="email"
                placeholder="Email"
                value={address.email || ""}
                onChange={handleChange}
                className={styles.wideField}
            />
            <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={address.phone || ""}
                onChange={handleChange}
                className={styles.wideField}
            />
            {children}
        </form>
    );
}

export default AddressForm; 